import React, { useCallback, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { organisationsAPI } from '../../organisations/api/organisationsAPI';
import { londonToday } from '../../organisations/lib/schoolYears';
import useTeacherDashboardData from '../hooks/useTeacherDashboardData';
import useOverviewData from '../hooks/useOverviewData';
import useChatSummary from '../hooks/useChatSummary';
import OverviewChatCard from './overview/OverviewChatCard';
import useDashboardParams from '../hooks/useDashboardParams';
import { DEFAULT_PERIOD, PERIOD_OPTIONS, isPeriod } from '../lib/format';
import { parseView } from '../lib/overviewView';
import { paperReviewHref, quizReviewHref } from '../lib/dashboardParams';
import { buildScopeSections, findScope } from '../lib/scopes';
import { groupErrorMessage } from '../lib/groupActions';
import ScopeSidebar from './scope/ScopeSidebar';
import EmptyState from './common/EmptyState';
import OverviewTab from './tabs/OverviewTab';
import StudentsTab from './tabs/StudentsTab';
import CertificatesTab from './tabs/CertificatesTab';
import SchoolTab from './tabs/SchoolTab';
import StudentProfile from './student/StudentProfile';
import { withAppBasename } from '../../../config/appBase';
import './TeacherDashboardPage.css';

const TABS = [
  { value: 'overview', label: 'Overview' },
  { value: 'students', label: 'Students' },
  { value: 'certificates', label: 'Certificates' },
  { value: 'school', label: 'Settings' },
];
const DEFAULT_TAB = 'overview';

// Tabs that show the selected scope's students. Picking a scope from any
// other tab moves to Students, as in the approved design.
const SCOPED_TABS = new Set(['overview', 'students']);

/**
 * The staff dashboard at /teacher-dashboard.
 *
 * Who may use it is the server's decision: GET /api/organisations/scopes
 * answers 403 for anyone who is neither staff of an organisation nor a
 * platform admin, and that is shown as "no access". Tab, scope, period,
 * open student, the Overview's open drill-down, the open paper or custom
 * quiz review, the profile's tab and the Activity section last moved to
 * live in the URL (?tab=, ?scope=, ?period=, ?student=, ?view=, ?paper=,
 * ?quiz=, ?ptab=, ?section=), so refresh, Back and shared links work.
 *
 * History: opening or closing a drill-down pushes an entry, so Back closes
 * it; switching bucket inside Needs attention, or segment inside
 * Documents, replaces it, so Back returns to the Overview rather than
 * stepping through each one. Opening a paper or quiz review pushes, so Back
 * returns to Activity, and so does choosing a profile tab, so Back returns
 * to the tab before. Moving to an Activity section replaces. Everything
 * else replaces, as before. Changing scope or period keeps the drill-down
 * open; changing tab closes it. Any change of student drops the paper,
 * the quiz, the profile tab and the section (lib/dashboardParams
 * mergeParams).
 */
const TeacherDashboardPage = () => {
  const { user, logout } = useAuth();
  const [searchParams, setParams] = useDashboardParams();
  const tab = searchParams.get('tab') || DEFAULT_TAB;
  const studentId = searchParams.get('student');
  const rawPeriod = searchParams.get('period');
  const period = isPeriod(rawPeriod) ? rawPeriod : DEFAULT_PERIOD;
  const view = tab === 'overview' ? parseView(searchParams.get('view')) : null;

  const {
    scopes, scopesLoaded, scopesError, forbidden, activeScope,
    students, periodLabel, studentsLoading, studentsError, reloadScopes, reloadStudents,
  } = useTeacherDashboardData({ scope: searchParams.get('scope'), period });

  const sections = useMemo(() => buildScopeSections(scopes, londonToday()), [scopes]);
  const selection = useMemo(() => findScope(sections, activeScope), [sections, activeScope]);

  const overviewEnabled = tab === 'overview' && Boolean(selection);
  const overviewData = useOverviewData({
    scope: activeScope, period, enabled: overviewEnabled,
  });
  const chatSummary = useChatSummary({
    scope: activeScope, period, enabled: overviewEnabled,
  });

  // Switching part inside a review replaces, so Back leaves the review.
  const openPaper = useCallback((ref, { replace = false } = {}) => (
    setParams({ paper: ref }, { push: Boolean(ref) && !replace })
  ), [setParams]);
  const paperHref = useCallback((ref) => paperReviewHref(searchParams, ref), [searchParams]);
  // A custom quiz review, opened as a paper review is; it closes any open paper.
  const openQuiz = useCallback((sessionId, { replace = false } = {}) => (
    setParams({ quiz: sessionId, paper: null }, { push: Boolean(sessionId) && !replace })
  ), [setParams]);
  const quizHref = useCallback((sessionId) => quizReviewHref(searchParams, sessionId), [searchParams]);

  useEffect(() => {
    if (!TABS.some((t) => t.value === tab)) setParams({ tab: DEFAULT_TAB });
  }, [tab, setParams]);

  useEffect(() => {
    if (rawPeriod != null && !isPeriod(rawPeriod)) setParams({ period: null });
  }, [rawPeriod, setParams]);

  // An open drill-down stays open for the new scope; leaving the Overview closes it.
  const selectScope = useCallback((scope) => {
    const patch = { scope, student: null, tab: SCOPED_TABS.has(tab) ? tab : 'students' };
    if (tab !== 'overview') patch.view = null;
    setParams(patch);
  }, [setParams, tab]);

  const createGroup = useCallback(async (organisationId, name) => {
    try {
      const { group } = await organisationsAPI.createGroup(organisationId, name);
      await reloadScopes();
      setParams({ tab: 'students', scope: group.scope, student: null, view: null });
      return null;
    } catch (err) {
      return groupErrorMessage(err, 'Could not create the group. Please try again.');
    }
  }, [reloadScopes, setParams]);

  const membershipChanged = useCallback(() => {
    reloadScopes();
    if (selection?.entry.kind === 'group') reloadStudents();
  }, [reloadScopes, reloadStudents, selection]);

  const groupDeleted = useCallback((organisationScope) => {
    setParams({ scope: organisationScope, student: null });
    reloadScopes();
  }, [setParams, reloadScopes]);

  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      window.location.href = withAppBasename('/');
    }
  };

  const header = (
    <header className="td-header">
      <div className="td-header__left">
        <span className="td-eyebrow">{selection ? `Staff · ${selection.section.organisation.name}` : 'Staff'}</span>
        <h1 className="td-header__title">Dashboard</h1>
      </div>
      <div className="td-header__right">
        {sections.length > 0 ? (
          <label className="td-select">
            <span className="td-select__label">Period</span>
            <select
              value={period}
              onChange={(e) => setParams({ period: e.target.value, student: null })}
            >
              {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        ) : null}
        <span className="td-header__user">{user?.email}</span>
        <button type="button" className="td-btn td-btn--ghost" onClick={handleSignOut}>Sign out</button>
      </div>
    </header>
  );

  if (!scopesLoaded) {
    return <div className="td-page">{header}<p className="td-loading">Loading…</p></div>;
  }

  if (forbidden) {
    return (
      <div className="td-page">
        {header}
        <div className="td-card td-card--centered">
          <h2 className="td-card__title">You do not have access</h2>
          <p className="td-card__body">
            This dashboard is for school staff. If you are staff, join your school with the
            staff code PathBuddy gave you.
          </p>
          <Link className="td-btn td-btn--primary" to="/join">Join your school</Link>
        </div>
      </div>
    );
  }

  if (scopesError) {
    return <div className="td-page">{header}<div className="td-error td-error--page">{scopesError}</div></div>;
  }

  if (sections.length === 0) {
    return (
      <div className="td-page">
        {header}
        <EmptyState title="No schools yet" body="Schools appear here once PathBuddy has set them up." />
      </div>
    );
  }

  const organisation = selection?.section.organisation;

  return (
    <div className="td-page">
      {header}

      <nav className="td-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            className={`td-tab${tab === t.value ? ' td-tab--active' : ''}`}
            onClick={() => setParams({ tab: t.value, student: null, view: null })}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="td-layout">
        <ScopeSidebar
          sections={sections}
          selectedScope={activeScope}
          onSelect={selectScope}
          onCreateGroup={createGroup}
        />

        <main className="td-main">
          {!selection ? (
            <EmptyState
              title="This list is not available"
              body="It may have been deleted. Choose one from the list on the left."
            />
          ) : null}

          {selection && tab === 'overview' ? (
            <>
              {overviewData.error ? <div className="td-error">{overviewData.error}</div> : null}
              {overviewData.loading ? <p className="td-loading">Loading…</p> : (
                <OverviewTab
                  students={overviewData.students}
                  overview={overviewData.overview}
                  documents={overviewData.documents}
                  roster={students}
                  rosterLoading={studentsLoading}
                  rosterError={studentsError}
                  period={period}
                  scope={activeScope}
                  scopeLabel={selection.entry.label}
                  studentCount={selection.entry.count}
                  view={view}
                  onOpenView={(next) => setParams({ view: next }, { push: true })}
                  onSwitchView={(next) => setParams({ view: next })}
                  onCloseView={() => setParams({ view: null }, { push: true })}
                  onOpenStudent={(id) => setParams({ tab: 'students', student: id, view: null })}
                />
              )}
              {!view ? (
                <OverviewChatCard
                  state={chatSummary}
                  onOpenStudent={(id) => setParams({ tab: 'students', student: id, view: null })}
                />
              ) : null}
            </>
          ) : null}

          {selection && tab === 'students' && studentId ? (
            <StudentProfile
              studentId={studentId}
              period={period}
              currentUserId={user?.id}
              onBack={() => setParams({ student: null })}
              paper={searchParams.get('paper')}
              onOpenPaper={openPaper}
              paperHref={paperHref}
              quiz={searchParams.get('quiz')}
              onOpenQuiz={openQuiz}
              quizHref={quizHref}
              ptab={searchParams.get('ptab')}
              section={searchParams.get('section')}
              onNavigate={setParams}
            />
          ) : null}

          {selection && tab === 'students' && !studentId ? (
            <StudentsTab
              key={activeScope}
              students={students}
              loading={studentsLoading}
              error={studentsError}
              selection={selection}
              periodLabel={periodLabel}
              onOpenStudent={(id) => setParams({ student: id })}
              onMembershipChanged={membershipChanged}
              onGroupDeleted={groupDeleted}
            />
          ) : null}

          {selection && tab === 'certificates' ? <CertificatesTab students={students} /> : null}

          {organisation && tab === 'school' ? <SchoolTab organisation={organisation} /> : null}
        </main>
      </div>
    </div>
  );
};

export default TeacherDashboardPage;
